import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Checkbox } from "@chakra-ui/react";
import { showToast, cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import Modal from "newui/components/ui/Modal/Modal";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { useInitialEvaluationContext } from "../../../hooks/InitialEvaluationContext";
import { getCompletionStatus } from "../../../api/scoring-api";
import { SupplierResponseTable } from "./SupplierResponseTable";
import { ScoringModal } from "./ScoringModal";
import { PanelEvaluationView } from "./panel/PanelEvaluationView";
import { PanelMemberScoringView } from "./panel/member/PanelMemberScoringView";
import { EvaluationClarificationsTable } from "./panel/clarifications/EvaluationClarificationsTable";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMemberStatus, PanelMode } from "../../../types/panel.type";
import { EvaluationStepNumber } from "../../../types/evaluation.type";

interface InitialEvaluationStepProps {
	currentStep: EvaluationStepNumber;
	rfxData: Rfx;
	evalId?: number;
	panelMode?: PanelMode | string | null;
	panelMemberId?: number;
	panelMemberStatus?: string;
	isLoading?: boolean;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

export const InitialEvaluationStep: React.FC<InitialEvaluationStepProps> = ({
	currentStep,
	rfxData,
	evalId,
	panelMode,
	panelMemberId,
	panelMemberStatus,
	isLoading = false,
	onBeforeNext,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	const rfxId = rfxData?.id;
	const currentUserId = useSelector(selectCurrentUserId);
	const isPanel = panelMode === "manual_panel" || panelMode === "ai_panel";
	const isRfxLead = currentUserId !== null && rfxData?.rfxLead?.id === currentUserId;

	// Panel mode: show panel-specific views
	if (isPanel && evalId) {
		if (isRfxLead) {
			return <PanelEvaluationView rfxData={rfxData} evalId={evalId} isRfxLead currentStep={currentStep} onCompletionChange={onCompletionChange} onRefreshUserContext={onRefreshUserContext} />;
		}

		if (panelMemberId && panelMemberStatus) {
			return (
				<PanelMemberScoringView
					currentStep={currentStep}
					rfxData={rfxData}
					evalId={evalId}
					memberId={panelMemberId}
					memberStatus={panelMemberStatus as PanelMemberStatus}
				/>
			);
		}
		// Non-lead, non-panel user in panel mode — show read-only via PanelEvaluationView
		return <PanelEvaluationView rfxData={rfxData} evalId={evalId} isRfxLead={false} />;
	}

	return (
		<SoloEvaluationView
			rfxData={rfxData}
			rfxId={rfxId}
			evalId={evalId}
			isLoading={isLoading}
			onBeforeNext={onBeforeNext}
			onCompletionChange={onCompletionChange}
		/>
	);
};

type SoloTabId = "my-scoring" | "my-clarifications";

interface SoloEvaluationViewProps {
	rfxData: Rfx;
	rfxId: number;
	evalId?: number;
	isLoading: boolean;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
}

const SoloEvaluationView: React.FC<SoloEvaluationViewProps> = ({
	rfxData,
	rfxId,
	evalId,
	isLoading,
	onBeforeNext,
	onCompletionChange,
}) => {
	const [activeTab, setActiveTab] = useState<SoloTabId>("my-scoring");
	const [selectedInviteId, setSelectedInviteId] = useState<number | null>(null);
	const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
	const [confirmChecked, setConfirmChecked] = useState(false);
	const pendingResolveRef = useRef<((value: boolean) => void) | null>(null);

	const {
		suppliers,
		isLoading: isSuppliersLoading,
		error,
		allSuppliersScored,
		readOnly,
		notPermitted,
		refreshSupplier,
	} = useInitialEvaluationContext();

	// Report completion state to parent for enabling/disabling the Next button
	useEffect(() => {
		onCompletionChange?.(allSuppliersScored);
	}, [allSuppliersScored, onCompletionChange]);

	const handleSelectSupplier = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleCloseModal = useCallback(() => {
		if (selectedInviteId !== null) {
			refreshSupplier(selectedInviteId);
		}
		setSelectedInviteId(null);
	}, [selectedInviteId, refreshSupplier]);

	const handleSupplierChange = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleScoringComplete = useCallback(() => {
		if (selectedInviteId !== null) {
			refreshSupplier(selectedInviteId);
		}
	}, [selectedInviteId, refreshSupplier]);

	const handleConfirmSubmit = useCallback(() => {
		setShowSubmitConfirm(false);
		setConfirmChecked(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(true);
			pendingResolveRef.current = null;
		}
	}, []);

	const handleCancelSubmit = useCallback(() => {
		setShowSubmitConfirm(false);
		setConfirmChecked(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
	}, []);

	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		try {
			const completion = await getCompletionStatus(rfxId);
			if (!completion.isComplete) {
				const { summary } = completion;
				showToast(
					`All suppliers must be fully scored before submitting. ${summary.fullyScored}/${summary.totalSuppliers} suppliers scored.`,
					"Evaluation Incomplete",
					false
				);
				return false;
			}
		} catch {
			showToast("Failed to check completion status. Please try again.", "Error", false);
			return false;
		}

		// Show confirmation dialog and wait for user response
		return new Promise<boolean>((resolve) => {
			pendingResolveRef.current = resolve;
			setShowSubmitConfirm(true);
		});
	}, [rfxId]);

	useEffect(() => {
		onBeforeNext?.(checkBeforeNext);
	}, [onBeforeNext, checkBeforeNext]);

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	if (notPermitted) {
		return (
			<div data-testid="initial-evaluation-not-permitted" className="space-y-4">
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
					<span className="mt-0.5 text-amber-600" aria-hidden="true">&#9888;</span>
					<div>
						<p className="text-sm font-medium text-amber-800">
							You are not a member of this evaluation panel
						</p>
						<p className="mt-1 text-sm text-amber-700">
							Only the RFx Lead
							{rfxData?.rfxLead?.fullName && ` (${rfxData.rfxLead.fullName})`}
							{" "}and invited panel members can score evaluations. Contact the RFx Lead if you need access.
						</p>
					</div>
				</div>
			</div>
		);
	}

	const showTabs = !!evalId;

	return (
		<div data-testid="initial-evaluation-step">
			<p className="text-sm text-gray-500 mb-4">
				{readOnly
					? `Viewing supplier submissions. Only the RFx Lead${rfxData?.rfxLead?.fullName && ` (${rfxData?.rfxLead?.fullName})`} or evaluation panel members can score evaluations.`
					: "Score each supplier against the evaluation criteria provided for this RFx."}
			</p>

			{error && (
				<p className="mb-4 text-sm text-red-600" data-testid="evaluation-error">
					{error}
				</p>
			)}

			{showTabs ? (
				<>
					<div className="flex border-b border-gray-200" data-testid="evaluation-tabs" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "my-scoring"}
							aria-controls="tab-panel-my-scoring"
							onClick={() => setActiveTab("my-scoring")}
							className={cn(
								"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
								activeTab === "my-scoring"
									? "border-[#0031DD] text-[#0031DD]"
									: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
							)}
							data-testid="tab-my-scoring"
						>
							My Scoring
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "my-clarifications"}
							aria-controls="tab-panel-my-clarifications"
							onClick={() => setActiveTab("my-clarifications")}
							className={cn(
								"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
								activeTab === "my-clarifications"
									? "border-[#0031DD] text-[#0031DD]"
									: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
							)}
							data-testid="tab-my-clarifications"
						>
							My Clarifications
						</button>
					</div>

					<div
						id="tab-panel-my-scoring"
						role="tabpanel"
						className={activeTab === "my-scoring" ? "mt-4 space-y-4" : "hidden"}
					>
						<SupplierResponseTable
							suppliers={suppliers}
							isLoading={isSuppliersLoading}
							rfxId={rfxId}
							onSelectSupplier={readOnly ? undefined : handleSelectSupplier}
							readOnly={readOnly}
						/>
					</div>

					<div
						id="tab-panel-my-clarifications"
						role="tabpanel"
						className={activeTab === "my-clarifications" ? "mt-4 space-y-4" : "hidden"}
					>
						<EvaluationClarificationsTable
							rfxId={rfxId}
							evalId={evalId!}
							suppliers={suppliers.map((s) => ({
								inviteId: s.inviteId,
								supplierName: s.supplierName,
							}))}
						/>
					</div>
				</>
			) : (
				<SupplierResponseTable
					suppliers={suppliers}
					isLoading={isSuppliersLoading}
					rfxId={rfxId}
					onSelectSupplier={handleSelectSupplier}
					readOnly={readOnly}
				/>
			)}

			{selectedInviteId !== null && !readOnly && (
				<ScoringModal
					isOpen={selectedInviteId !== null}
					rfxId={rfxId}
					suppliers={suppliers}
					selectedInviteId={selectedInviteId}
					onClose={handleCloseModal}
					onSupplierChange={handleSupplierChange}
					onScoringComplete={handleScoringComplete}
				/>
			)}

			{showSubmitConfirm && (
				<Modal
					isOpen={showSubmitConfirm}
					onClose={handleCancelSubmit}
					title="Submit Evaluations"
					size="md"
					iconType="warning"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick={false}
					primaryButton={{
						text: "Submit",
						onClick: handleConfirmSubmit,
						disabled: !confirmChecked,
						width: "full"
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: handleCancelSubmit,
						width: "full"
					}}
				>
					<div className="space-y-4">
						<p className="text-sm text-gray-600 text-center">
							Once submitted, your evaluation scores and comments cannot be edited.
							Please ensure all scores are accurate before proceeding.
						</p>
						<label
							className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
							data-testid="submit-confirm-checkbox"
						>
							<Checkbox
								isChecked={confirmChecked}
								onChange={(e) => setConfirmChecked(e.target.checked)}
								colorScheme="blue"
								mt={0.5}
							/>
							<span className="text-sm text-gray-700">
								I confirm that I have completed all evaluations to the best of my knowledge
								and understand that scores cannot be changed after submission.
							</span>
						</label>
					</div>
				</Modal>
			)}
		</div>
	);
};
